import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useI18n } from '../../../i18n/I18nContext';
import './GpsHistory.css';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

import { getChauffeurs } from '../../../services/chauffeurService';
import api from '../../../services/api';
import { getChauffeurGpsHistory, getMissionGpsHistory } from '../../../services/positionService';
import MissionGpsTimeline from './MissionGpsTimeline';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const GpsHistory = () => {
    const { t } = useI18n();
    const [activeLeftTab, setActiveLeftTab] = useState('map'); // 'map' | 'timeline'
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        month: new Date().toISOString().slice(0, 7), // YYYY-MM
        chauffeurId: 'all',
        vehiculeId: 'all'
    });
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [routeCoordinates, setRouteCoordinates] = useState([]);
    const [tripHistory, setTripHistory] = useState([]);
    const routeCoordsRef = useRef([]);
    const tripHistoryRef = useRef([]);

    const [refreshNonce, setRefreshNonce] = useState(0);
    const triggerRefresh = () => setRefreshNonce(n => n + 1);

    // Keep refs in sync
    useEffect(() => {
        routeCoordsRef.current = routeCoordinates;
        tripHistoryRef.current = tripHistory;
    }, [routeCoordinates, tripHistory]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [missions, setMissions] = useState([]);
    const [selectedMission, setSelectedMission] = useState(null);
    const [fullHistory, setFullHistory] = useState([]);
    const [cache, setCache] = useState({}); // { [cacheKey]: { history, missions } }

    const mapContainer = useRef(null);
    const map = useRef(null);
    const marker = useRef(null);
    const animationRef = useRef(null);
    const speedMarkersRef = useRef([]);

    // Fetch initial data
    useEffect(() => {
        const loadData = async () => {
            try {
                const [driversData, vehiclesData] = await Promise.all([
                    getChauffeurs(),
                    api.get('/transporteur/vehicules')
                ]);
                setDrivers(Array.isArray(driversData) ? driversData : []);
                setVehicles(Array.isArray(vehiclesData.data) ? vehiclesData.data : []);
            } catch (err) {
                console.error('Error loading GPS history data:', err);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    // Fetch history (missions + full month GPS) when driver or filters change
    useEffect(() => {
        if (!selectedDriver) {
            setMissions([]);
            setFullHistory([]);
            setSelectedMission(null);
            setRouteCoordinates([]);
            setTripHistory([]);
            return;
        }

        const loadHistory = async () => {
            const cacheKey = `${selectedDriver.id}-${filters.month}`;

            // Best Practice: Always refresh cache if we're looking at the current month or refresh explicitly requested
            const isCurrentMonth = filters.month === new Date().toISOString().slice(0, 7);
            if (cache[cacheKey] && !isCurrentMonth && !refreshNonce) {
                const cached = cache[cacheKey];
                setMissions(cached.missions);
                setFullHistory(cached.history);
                setSelectedMission(null);
                return;
            }

            setIsHistoryLoading(true);
            try {
                const [year, month] = filters.month.split('-').map(Number);
                const start = new Date(year, month - 1, 1, 0, 0, 0).toISOString();
                const end = new Date(year, month, 0, 23, 59, 59).toISOString();

                const [history, missionsRes] = await Promise.all([
                    getChauffeurGpsHistory(selectedDriver.id, start, end),
                    api.get(`/transporteur/drivers/${selectedDriver.id}/missions`)
                ]);

                const allMissions = Array.isArray(missionsRes.data) ? missionsRes.data : [];
                const monthStart = new Date(year, month - 1, 1).getTime();
                const monthEnd = new Date(year, month, 0, 23, 59, 59).getTime();

                const monthMissions = allMissions.filter(m => {
                    const mStart = new Date(m.departureDate || m.plannedStartAt).getTime();
                    const isWithinMonth = mStart >= monthStart && mStart <= monthEnd;
                    const isActive = m.status === 'EN_COURS' || m.status === 'EN_PAUSE';
                    
                    // Show if starts in month OR is currently active (best practice for overlapping missions)
                    return isWithinMonth || isActive;
                }).sort((a, b) => new Date(b.departureDate || b.plannedStartAt).getTime() - new Date(a.departureDate || a.plannedStartAt).getTime());

                setCache(prev => ({
                    ...prev,
                    [cacheKey]: { history: history || [], missions: monthMissions }
                }));

                setFullHistory(history || []);
                setMissions(monthMissions);
                setSelectedMission(null);
            } catch (err) {
                console.error('Error loading chauffeur history:', err);
                setFullHistory([]);
                setMissions([]);
            } finally {
                setIsHistoryLoading(false);
            }
        };
        loadHistory();
    }, [selectedDriver, filters.month, refreshNonce]);

    const processTrajectory = (data) => {
        // Sample coordinates if there are too many (e.g., > 2000) for performance
        let sampledData = data;
        if (data.length > 2000) {
            const step = Math.ceil(data.length / 2000);
            sampledData = data.filter((_, i) => i % step === 0);
        }

        // Filter out highly suspicious points [0,0] (Gulf of Guinea acquisitions)
        const validData = sampledData.filter(p => {
            const lat = parseFloat(p.latitude);
            const lng = parseFloat(p.longitude);
            return !isNaN(lat) && !isNaN(lng) &&
                (Math.abs(lat) > 0.0001 || Math.abs(lng) > 0.0001); // More permissive
        });

        const coords = validData.map(p => [p.longitude, p.latitude]);
        setRouteCoordinates(coords);
        setTripHistory(validData);
    };

    // Unified trajectory computation logic
    useEffect(() => {
        setIsPlaying(false);
        setCurrentStep(0);

        if (!selectedMission) {
            // Case 1: Overview mode - show all points for the month
            if (fullHistory.length > 0) {
                processTrajectory(fullHistory);
            } else {
                setRouteCoordinates([]);
                setTripHistory([]);
            }
            return;
        }

        // Case 2: Mission-specific filtering
        const startVal = selectedMission.departureDate || selectedMission.plannedStartAt;
        const mStart = startVal ? new Date(startVal).getTime() : 0;
        const mEnd = selectedMission.arrivalDate ? new Date(selectedMission.arrivalDate).getTime() : Date.now();

        if (mStart === 0) {
            processTrajectory(fullHistory);
            return;
        }

        const filtered = fullHistory.filter(p => {
            if (!p.horodatage) return false;
            const timestamp = new Date(p.horodatage).getTime();
            return !isNaN(timestamp) && timestamp >= mStart && timestamp <= mEnd;
        });

        if (filtered.length > 0) {
            processTrajectory(filtered);
        } else {
            // SaaS Best Practice: If no points in month cache, fetch specifically for mission (could be across month boundary)
            const loadFallback = async () => {
                try {
                    const missionHistory = await getMissionGpsHistory(selectedMission.id);
                    if (missionHistory.length > 0) {
                        processTrajectory(missionHistory);
                    } else {
                        setRouteCoordinates([]);
                        setTripHistory([]);
                    }
                } catch (err) {
                    console.error('Fallback fetch failed:', err);
                    setRouteCoordinates([]);
                    setTripHistory([]);
                }
            };
            loadFallback();
        }
    }, [selectedMission, fullHistory]);

    // Handle filter changes
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        if (field === 'chauffeurId' && value !== 'all') {
            const driver = drivers.find(d => d.id === parseInt(value));
            if (driver) setSelectedDriver(driver);
        }
    };

    // Stable Map Update Function
    const updateMapData = React.useCallback(() => {
        const coords = routeCoordsRef.current;
        const history = tripHistoryRef.current;

        if (!map.current || !map.current.isStyleLoaded()) return;

        // If no coordinates, hide marker and return early
        if (coords.length === 0) {
            if (marker.current) marker.current.remove();
            marker.current = null;
            return;
        }

        // Lazy initialize marker only when data is present
        if (!marker.current) {
            const el = document.createElement('div');
            el.className = 'vehicle-marker';
            marker.current = new mapboxgl.Marker(el)
                .setLngLat(coords[0])
                .addTo(map.current);
        }

        const source = map.current.getSource('route');
        if (source) {
            source.setData({
                'type': 'Feature',
                'properties': {},
                'geometry': {
                    'type': 'LineString',
                    'coordinates': coords
                }
            });

            speedMarkersRef.current.forEach(m => m.remove());
            speedMarkersRef.current = [];

            history.forEach(p => {
                if (p.speed > 100) {
                    const el = document.createElement('div');
                    el.className = 'alert-marker';
                    const m = new mapboxgl.Marker(el)
                        .setLngLat([p.longitude, p.latitude])
                        .setPopup(new mapboxgl.Popup().setHTML(`<h6>Survitesse détectée</h6><p>${Math.round(p.speed)} km/h @ ${new Date(p.horodatage).toLocaleTimeString()}</p>`))
                        .addTo(map.current);
                    speedMarkersRef.current.push(m);
                }
            });

            if (marker.current) marker.current.setLngLat(coords[0]);

            const bounds = new mapboxgl.LngLatBounds();
            coords.forEach(c => bounds.extend(c));
            if (coords.length > 0) {
                map.current.resize();
                map.current.fitBounds(bounds, { padding: 50, duration: 1000 });
            }
        }
    }, []);

    // Initialize Map Instance (Persists while driver is selected)
    useEffect(() => {
        if (!selectedDriver || !mapContainer.current) return;

        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: routeCoordsRef.current?.[0] || [-1.6508, 48.1173], // Default to a more interesting location or first point
            zoom: routeCoordsRef.current?.[0] ? 13 : 5
        });

        map.current.on('load', () => {
            map.current.addSource('route', {
                'type': 'geojson',
                'data': {
                    'type': 'FeatureCollection',
                    'features': []
                }
            });

            map.current.addLayer({
                'id': 'route',
                'type': 'line',
                'source': 'route',
                'layout': { 'line-join': 'round', 'line-cap': 'round' },
                'paint': { 'line-color': '#4285F4', 'line-width': 6, 'line-opacity': 0.8 }
            });

            updateMapData();
        });

        map.current.on('style.load', updateMapData);

        const handleResize = () => {
            if (map.current) map.current.resize();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            speedMarkersRef.current.forEach(m => m.remove());
            speedMarkersRef.current = [];
        };
    }, [selectedDriver?.id, updateMapData]);

    // Efficient Data Updates (on existing map)
    useEffect(() => {
        updateMapData();
    }, [routeCoordinates, updateMapData]);

    // Playback Animation Loop
    useEffect(() => {
        if (!isPlaying || routeCoordinates.length < 2) return;

        const animate = () => {
            setCurrentStep(prev => {
                const coords = routeCoordsRef.current;
                const next = prev + (0.01 * playbackSpeed);
                if (next >= coords.length - 1) {
                    setIsPlaying(false);
                    return prev;
                }

                const index = Math.floor(next);
                const fraction = next - index;
                const p1 = coords[index];
                const p2 = coords[index + 1];

                if (!p1 || !p2) return prev;

                const lng = p1[0] + (p2[0] - p1[0]) * fraction;
                const lat = p1[1] + (p2[1] - p1[1]) * fraction;

                if (marker.current) {
                    marker.current.setLngLat([lng, lat]);
                }

                return next;
            });
            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationRef.current);
    }, [isPlaying, playbackSpeed, routeCoordinates]);

    // Handle Resize on Layout Change
    useEffect(() => {
        if (map.current) {
            const timer = setTimeout(() => {
                map.current.resize();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [!!selectedDriver]);

    const handleBack = () => {
        setIsPlaying(false);
        setCurrentStep(0);
        setSelectedDriver(null);
        setSelectedMission(null);
    };

    const chartData = useMemo(() => {
        const filtered = tripHistory.filter((_, i) => i % 5 === 0);
        return {
            labels: filtered.map(p => {
                try {
                    return new Date(p.horodatage).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                } catch {
                    return '';
                }
            }),
            datasets: [
                {
                    label: 'Vitesse (km/h)',
                    data: filtered.map(p => (p.speed || 0) * 3.6),
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    borderWidth: 3,
                    fill: 'start',
                    tension: 0.4,
                    pointRadius: 0,
                    pointHoverRadius: 6,
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: '#3b82f6',
                    pointHoverBorderWidth: 3,
                }
            ]
        };
    }, [tripHistory]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#1e293b',
                bodyColor: '#1e293b',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                padding: 12,
                displayColors: false,
                bodyFont: { weight: 'bold', size: 13 },
                titleFont: { size: 11 },
                callbacks: {
                    label: (context) => `⚡ ${context.parsed.y} km/h`
                }
            }
        },
        scales: {
            y: { 
                beginAtZero: true, 
                grid: { display: true, color: '#f1f5f9', drawBorder: false }, 
                ticks: { color: '#94a3b8', font: { size: 10, weight: '600' } } 
            },
            x: { 
                grid: { display: false }, 
                ticks: { color: '#94a3b8', font: { size: 10, weight: '600' }, maxRotation: 0 } 
            }
        }
    };

    if (isLoading) {
        return (
            <DashboardLayout hideSidebar={!!selectedDriver}>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '25rem' }}>{/* 400px / 16 = 25rem */}
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout hideSidebar={!!selectedDriver}>
            <div className={`gps-history-container ${selectedDriver ? 'full-screen-mode' : ''}`}>
                {!selectedDriver ? (
                    <>
                        <div className="gps-header mb-4">
                            <div>
                                <h2 className="fw-bold">{t('common.gpsHistory')}</h2>
                                <p className="text-muted">Suivi et historique des trajectoires de votre flotte</p>
                            </div>
                        </div>

                        {/* Filter Bar */}
                        <div className="filter-bar shadow-sm mb-4">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold">Mois de l'historique</label>
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={filters.month}
                                        onChange={(e) => handleFilterChange('month', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold">Chauffeur</label>
                                    <select
                                        className="form-select"
                                        value={selectedDriver?.id || 'all'}
                                        onChange={(e) => handleFilterChange('chauffeurId', e.target.value)}
                                    >
                                        <option value="all">Tous les chauffeurs</option>
                                        {drivers.map(d => <option key={d.id} value={d.id}>{d.firstname} {d.lastname}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold">Véhicule</label>
                                    <select
                                        className="form-select"
                                        value={filters.vehiculeId}
                                        onChange={(e) => handleFilterChange('vehiculeId', e.target.value)}
                                    >
                                        <option value="all">Tous les véhicules</option>
                                        {vehicles.map(v => <option key={v.id} value={v.id}>{v.plateNumber}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold">Recherche rapide</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Nom, plaque..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Drivers Overview */}
                        <div className="row g-4">
                            {drivers.filter(d =>
                                `${d.firstname} ${d.lastname}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                (d.email && d.email.toLowerCase().includes(searchQuery.toLowerCase()))
                            ).length > 0 ? (
                                drivers.filter(d =>
                                    `${d.firstname} ${d.lastname}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    (d.email && d.email.toLowerCase().includes(searchQuery.toLowerCase()))
                                ).map(driver => (
                                    <div key={driver.id} className="col-xl-3 col-lg-4 col-md-6">
                                        <div className="driver-card shadow-sm border-0 h-100">
                                            <div className="card-body p-4">
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <div className="driver-avatar">
                                                        {driver.firstname?.[0]}{driver.lastname?.[0]}
                                                    </div>
                                                    <span className={`status-badge ${driver.connectionStatus === 'ONLINE' ? 'online' : 'offline'}`}>
                                                        {driver.connectionStatus === 'ONLINE' ? '● En ligne' : '○ Hors ligne'}
                                                    </span>
                                                </div>
                                                <h5 className="fw-bold mb-1">{driver.firstname} {driver.lastname}</h5>
                                                <p className="text-primary small fw-bold mb-3">{driver.telephone}</p>

                                                <div className="location-info mb-3">
                                                    <i className="bi bi-geo-alt-fill text-muted me-2"></i>
                                                    <span className="small text-muted">{driver.shiftStatus === 'EN_POSTE' ? 'En poste' : 'Hors poste'}</span>
                                                    <div className="small text-muted mt-1 ms-4">{driver.disponible ? 'Disponible' : 'Occupé'}</div>
                                                </div>

                                                <div className="row g-0 border-top pt-3 mt-3">
                                                    <div className="col-6 border-end text-center">
                                                        <div className="small text-muted">ID</div>
                                                        <div className="fw-bold">#{driver.id}</div>
                                                    </div>
                                                    <div className="col-6 ps-3 text-center">
                                                        <div className="small text-muted">Statut</div>
                                                        <div className="fw-bold small">{driver.active ? 'Actif' : 'Inactif'}</div>
                                                    </div>
                                                </div>

                                                <button
                                                    className="btn btn-primary w-100 mt-4 rounded-pill fw-bold"
                                                    onClick={() => setSelectedDriver(driver)}
                                                >
                                                    Voir l'historique GPS
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 text-center py-5">
                                    <div className="text-muted mb-3">
                                        <i className="bi bi-person-slash fs-1"></i>
                                    </div>
                                    <h5 className="text-muted">Aucun chauffeur trouvé</h5>
                                    <p className="text-muted small">Assurez-vous d'avoir ajouté des chauffeurs à votre flotte.</p>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="gps-detail-view h-100">
                        <div className="d-flex align-items-center mb-4">
                            <button className="btn btn-link link-dark p-0 me-3" onClick={handleBack}>
                                <i className="bi bi-arrow-left fs-4"></i>
                            </button>
                            <h3 className="mb-0 fw-bold me-auto">Historique GPS : {selectedDriver.firstname} {selectedDriver.lastname}</h3>
                            <button 
                                className={`btn btn-outline-primary rounded-pill btn-sm fw-bold px-3 ${isHistoryLoading ? 'disabled' : ''}`}
                                onClick={triggerRefresh}
                            >
                                {isHistoryLoading ? (
                                    <><span className="spinner-border spinner-border-sm me-2"></span>Chargement...</>
                                ) : (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Rafraîchir</>
                                )}
                            </button>
                        </div>

                        <div className="row g-0 flex-grow-1 overflow-hidden detail-view">
                            {/* Left Panel */}
                            <div className="col-md-4 left-panel p-4 bg-white shadow-sm overflow-auto">
                                <div className="driver-summary mb-4 p-3 bg-light rounded shadow-sm">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="driver-avatar-sm me-3">{selectedDriver.firstname?.[0]}{selectedDriver.lastname?.[0]}</div>
                                        <div>
                                            <div className="fw-bold fs-5">{selectedDriver.firstname} {selectedDriver.lastname}</div>
                                            <div className="small text-primary fw-bold">{selectedDriver.telephone}</div>
                                        </div>
                                    </div>
                                    <div className="row g-2 text-center">
                                        <div className="col-4">
                                            <div className="label-xs text-muted fw-bold">POINTS</div>
                                            <div className="fw-bold">{tripHistory.length}</div>
                                        </div>
                                        <div className="col-4 border-start">
                                            <div className="label-xs text-muted fw-bold">MISSIONS</div>
                                            <div className="fw-bold">{missions.length}</div>
                                        </div>
                                        <div className="col-4 border-start">
                                            <div className="label-xs text-muted fw-bold">MOIS</div>
                                            <div className="fw-bold small text-uppercase">{new Date(filters.month).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="trip-list mt-4">
                                    <h6 className="fw-bold mb-3 text-uppercase small text-muted">Aperçu du mois ({new Date(filters.month).toLocaleDateString('fr-FR', { month: 'long' })})</h6>

                                    <div className="mission-selection-grid mb-4">
                                        <div
                                            className={`mission-item p-3 mb-2 rounded border-start border-4 shadow-sm cursor-pointer ${!selectedMission ? 'border-primary bg-primary-subtle' : 'border-light bg-white'}`}
                                            onClick={() => { setSelectedMission(null); setActiveLeftTab('map'); }}
                                        >
                                            <div className="fw-bold small">Tous les mouvements du mois</div>
                                            <div className="extra-small text-muted">{fullHistory.length} points GPS enregistrés</div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold mb-0 text-uppercase small text-muted">Missions effectuées</h6>
                                        <span className="badge bg-secondary extra-small">{missions.length} missions</span>
                                    </div>

                                    <div className="trip-scroll-area">
                                        {isHistoryLoading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                                <p className="small text-muted mt-2">Chargement...</p>
                                            </div>
                                        ) : missions.length > 0 ? (
                                            missions.map((m, idx) => (
                                                <div
                                                    key={m.id}
                                                    className={`mission-item p-3 mb-2 rounded border-start border-4 shadow-sm cursor-pointer ${selectedMission?.id === m.id ? 'border-primary bg-primary-subtle' : 'border-success bg-white'}`}
                                                    onClick={() => setSelectedMission(m)}
                                                >
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <span className="fw-bold small text-truncate" style={{ maxWidth: '11.25rem' }}>{/* 180px / 16 = 11.25rem */}
                                                            {m.departureLocation} → {m.arrivalLocation}
                                                        </span>
                                                        <span className="badge bg-light text-dark extra-small">#{m.id.substring(0, 6)}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between text-muted extra-small">
                                                        <span><i className="bi bi-clock me-1"></i> {new Date(m.departureDate).toLocaleDateString()}</span>
                                                        <span>{m.status}</span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center p-4 text-muted bg-light rounded">
                                                <i className="bi bi-truck fs-2 mb-2"></i>
                                                <p className="small mb-0">Aucune mission trouvée pour ce mois</p>
                                            </div>
                                        )}
                                    </div>

                                    {selectedMission && (
                                        <div className="mt-4">
                                            {/* Tab switcher */}
                                            <ul className="nav nav-tabs nav-tabs-sm mb-3">
                                                <li className="nav-item">
                                                    <button
                                                        className={`nav-link py-1 px-3 small fw-bold ${activeLeftTab === 'map' ? 'active' : ''}`}
                                                        onClick={() => setActiveLeftTab('map')}
                                                    >
                                                        <i className="bi bi-map me-1"></i>Analyse vitesse
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button
                                                        className={`nav-link py-1 px-3 small fw-bold ${activeLeftTab === 'timeline' ? 'active' : ''}`}
                                                        onClick={() => setActiveLeftTab('timeline')}
                                                    >
                                                        <i className="bi bi-list-check me-1"></i>Timeline GPS
                                                    </button>
                                                </li>
                                            </ul>

                                            {activeLeftTab === 'map' && (
                                                <div className="mission-analytics-box p-3 shadow-sm animate-fade-in border-0" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h6 className="fw-bold small text-muted text-uppercase mb-0">Analyse de la vitesse</h6>
                                                        <span className="badge bg-primary-subtle text-primary border-0 rounded-pill px-3">En temps réel</span>
                                                    </div>
                                                    <div className="speed-chart-container" style={{ height: '12rem', background: 'transparent', border: 'none' }}>
                                                        <Line data={chartData} options={chartOptions} />
                                                    </div>
                                                </div>
                                            )}

                                            {activeLeftTab === 'timeline' && (
                                                <div className="animate-fade-in" style={{ flexGrow: 1, minHeight: 0 }}>
                                                    <MissionGpsTimeline missionId={selectedMission.id} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Map Section */}
                            <div className="col-md-8 position-relative">
                                <div ref={mapContainer} className="map-view-container h-100" />

                                {isHistoryLoading && (
                                    <div className="map-loading-overlay position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center z-index-1">
                                        <div className="spinner-grow text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                                        <div className="h5 fw-bold text-primary mb-1">Synchronisation des données...</div>
                                        <div className="small text-muted px-4 text-center">Reconstitution de la trajectoire haute précision en cours.</div>
                                    </div>
                                )}

                                {!isHistoryLoading && routeCoordinates.length === 0 && selectedDriver && (
                                    <div className="map-empty-state-overlay position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white bg-opacity-50 z-index-1">
                                        <div className="empty-map-icon mb-3">
                                            <i className="bi bi-geo-alt text-muted" style={{ fontSize: '3rem' }}></i>
                                        </div>
                                        <div className="fw-bold text-dark">Aucune donnée télématique disponible</div>
                                        <div className="small text-muted">{selectedMission ? "Cette mission n'a pas enregistré de points GPS." : "Aucun mouvement enregistré pour ce chauffeur ce mois-ci."}</div>
                                    </div>
                                )}

                                {/* Trip Playback Controls */}
                                {routeCoordinates.length > 0 && !isHistoryLoading && (
                                    <div className="playback-controls shadow rounded-pill bg-white p-2 d-flex align-items-center">
                                        <button className="btn btn-sm btn-link text-primary" onClick={() => setIsPlaying(!isPlaying)}>
                                            <i className={`bi bi-${isPlaying ? 'pause' : 'play'}-fill fs-4`}></i>
                                        </button>
                                        <div className="divider mx-2"></div>
                                        <div className="speed-selector d-flex gap-1">
                                            {[1, 2, 4].map(speed => (
                                                <button
                                                    key={speed}
                                                    className={`btn btn-xs rounded-pill ${playbackSpeed === speed ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                    onClick={() => setPlaybackSpeed(speed)}
                                                >
                                                    {speed}x
                                                </button>
                                            ))}
                                        </div>
                                        <div className="divider mx-2"></div>
                                        <div className="timestamp small text-muted fw-bold">
                                            {tripHistory[Math.floor(currentStep)]?.horodatage ? new Date(tripHistory[Math.floor(currentStep)].horodatage).toLocaleString('fr-FR') : ''}
                                        </div>
                                    </div>
                                )}

                                {/* Map Floating Analytics */}
                                {routeCoordinates.length > 0 && !isHistoryLoading && (
                                    <div className="map-analytics shadow bg-white p-3 rounded-4 border-0">
                                        <div className="row g-3">
                                            <div className="col-6">
                                                <div className="label-xs text-muted fw-bold">VITESSE</div>
                                                <div className="h3 mb-0 fw-bold text-primary">{Math.round(tripHistory[Math.floor(currentStep)]?.speed || 0)} <small className="h6 text-muted">km/h</small></div>
                                            </div>
                                            <div className="col-6">
                                                <div className="label-xs text-muted fw-bold">DIRECTION</div>
                                                <div className="h3 mb-0 fw-bold">{Math.round(tripHistory[Math.floor(currentStep)]?.heading || 0)} <small className="h6 text-muted">°</small></div>
                                            </div>
                                        </div>
                                        <div className="progress mt-3 rounded-pill" style={{ height: '0.375rem' }}>{/* 6px / 16 = 0.375rem */}
                                            <div className="progress-bar progress-bar-animated" role="progressbar" style={{ width: `${(currentStep / (routeCoordinates.length - 1)) * 100}%` }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default GpsHistory;
